/**
 * Formulario para crear una tarea nueva (quien crea ve todo editable).
 * Editar una existente se hace campo por campo en `VistaTarea`.
 *
 * Es un componente de presentación: maneja los campos y le entrega al padre los datos listos
 * para enviar a la API (`onGuardar`). Quién guarda y cómo lo decide el padre.
 */
import { useState, type FormEvent } from 'react';
import { Giro } from '../../componentes/Esqueleto';
import { Icono } from '../../componentes/Icono';
import { diaEnChile } from '../../lib/formato';
import { useEtiquetaPrioridad } from '../configuracion/hooks';
import { SelectorProyecto } from '../proyectos/SelectorProyecto';
import { IconoTipo } from './IconoTipo';
import { EtiquetaEsfuerzo, SelectorEsfuerzo } from './SelectorEsfuerzo';
import { TareasParecidas } from './TareasParecidas';
import {
  ESTADOS_DEL_FLUJO,
  ETIQUETA_ESTADO,
  ETIQUETA_TIPO,
  PRIORIDADES,
  TIPOS,
  errorFechaInicio,
  errorFechaLimite,
  formatearNumero,
  type CrearTareaEntrada,
  type EstadoDelFlujo,
  type Prioridad,
  type Esfuerzo,
  type Tipo,
  type Usuario,
} from '@cdtec-backlog/contrato';

/** Valores de los campos tal como los maneja el formulario (todo texto o marca). */
export interface ValoresFormulario {
  titulo: string;
  descripcion: string;
  criterio: string;
  tipo: Tipo;
  prioridad: Prioridad;
  esfuerzo: Esfuerzo;
  /** Una tarea nueva no nace descartada: solo estados del flujo. */
  estado: EstadoDelFlujo;
  /** Id de la persona en texto, o vacío para «sin asignar». */
  responsableId: string;
  /** Id del proyecto en texto, o vacío para «Sin proyecto». */
  proyectoId: string;
  /** Id de la tarea madre en texto (ADR-0018), o vacío si no es subtarea. */
  padreId: string;
  pedidoPor: string;
  tocaRiego: boolean;
  requiereProduccion: boolean;
  detalleProduccion: string;
  enlace: string;
  /** Fecha límite `AAAA-MM-DD` (CDT-76), o vacío para «sin fecha». La puede poner cualquiera que crea. */
  fechaLimite: string;
  /** Solo ADMIN: día de inicio `AAAA-MM-DD`, o vacío para «hoy». */
  fechaInicio: string;
  /** Solo ADMIN y con estado «Hecho»: día de cierre `AAAA-MM-DD`, o vacío para «hoy». */
  fechaCierre: string;
}

export const VALORES_NUEVA_TAREA: ValoresFormulario = {
  titulo: '',
  descripcion: '',
  criterio: '',
  tipo: 'REQUERIMIENTO',
  prioridad: 'P2',
  esfuerzo: 'MEDIA',
  estado: 'NUEVO',
  responsableId: '',
  proyectoId: '',
  padreId: '',
  pedidoPor: '',
  tocaRiego: false,
  requiereProduccion: false,
  detalleProduccion: '',
  enlace: '',
  fechaLimite: '',
  fechaInicio: '',
  fechaCierre: '',
};

/**
 * Convierte lo que escribió la persona en lo que la API espera. Las fechas solo se mandan si la persona
 * es ADMIN y las escribió (la de cierre, además, solo con estado «Hecho»): la API decide el permiso.
 */
export function aEntradaApi(valores: ValoresFormulario, esAdmin = false): CrearTareaEntrada {
  const { fechaInicio, fechaCierre, fechaLimite, ...resto } = valores;
  return {
    ...resto,
    fechaLimite: fechaLimite || null,
    ...(esAdmin && fechaInicio ? { creadoEn: fechaInicio } : {}),
    ...(esAdmin && fechaCierre && valores.estado === 'HECHO' ? { cerradaEn: fechaCierre } : {}),
    titulo: valores.titulo.trim(),
    responsableId: valores.responsableId === '' ? null : Number(valores.responsableId),
    proyectoId: valores.proyectoId === '' ? null : Number(valores.proyectoId),
    padreId: valores.padreId === '' ? null : Number(valores.padreId),
  };
}

type CampoFecha = 'creadoEn' | 'cerradaEn' | 'fechaLimite';

/** Errores de las fechas (cliente y API) por campo. */
export type ErroresFechas = Partial<Record<CampoFecha, string>>;

/** Mismas reglas que la API (funciones del contrato); devuelve los errores o un objeto vacío. */
export function validarFechas(valores: ValoresFormulario): ErroresFechas {
  const errores: ErroresFechas = {};
  const { fechaInicio, fechaCierre } = valores;
  if (fechaInicio) {
    const error = errorFechaInicio(fechaInicio);
    if (error) errores.creadoEn = error;
  }
  if (fechaCierre && valores.estado === 'HECHO') {
    const error = errorFechaInicio(fechaCierre);
    if (error) errores.cerradaEn = error;
    else if (fechaInicio && !errores.creadoEn && fechaCierre < fechaInicio) {
      errores.cerradaEn = 'La fecha de cierre no puede ser anterior a la fecha de inicio.';
    }
  }
  return errores;
}

interface Props {
  /** Solo un administrador ve y puede fijar las fechas de inicio y de cierre (la API lo exige igual). */
  esAdmin?: boolean;
  /** Errores de la API por campo, tras un intento de guardar. */
  erroresApi?: ErroresFechas;
  valoresIniciales: ValoresFormulario;
  personas: Usuario[];
  etiquetaBoton: string;
  /** Texto del botón mientras guarda (con giro y deshabilitado: evita el doble envío). */
  etiquetaGuardando?: string;
  guardando: boolean;
  onGuardar: (entrada: CrearTareaEntrada) => void;
  onCancelar: () => void;
  /** Si viene, bajo el título se muestran «Tareas parecidas» y un clic abre esa tarea. */
  onAbrirParecida?: (id: number) => void;
  /**
   * Tarea madre ya elegida («Nueva subtarea» desde su ficha): se muestra arriba y se puede quitar. El id va en
   * `valoresIniciales.padreId`.
   */
  padre?: { id: number; numero: number; titulo: string } | null;
}

export function FormularioTarea({
  esAdmin = false,
  erroresApi = {},
  valoresIniciales,
  personas,
  etiquetaBoton,
  etiquetaGuardando = 'Guardando…',
  guardando,
  onGuardar,
  onCancelar,
  onAbrirParecida,
  padre = null,
}: Props) {
  const etiquetaPrioridad = useEtiquetaPrioridad();
  const [valores, setValores] = useState(valoresIniciales);
  const cambiar = <K extends keyof ValoresFormulario>(campo: K, valor: ValoresFormulario[K]) =>
    setValores((actuales) => ({ ...actuales, [campo]: valor }));

  const [erroresCliente, setErroresCliente] = useState<ErroresFechas>({});
  const hoy = diaEnChile(new Date());
  const pideCierre = esAdmin && valores.estado === 'HECHO';
  const errorInicio = erroresCliente.creadoEn ?? erroresApi.creadoEn;
  const errorCierre = erroresCliente.cerradaEn ?? erroresApi.cerradaEn;
  const errorLimite = erroresCliente.fechaLimite ?? erroresApi.fechaLimite;

  const cambiarFecha = (campo: 'fechaInicio' | 'fechaCierre', valor: string) => {
    setErroresCliente({});
    cambiar(campo, valor);
  };

  const alEnviar = (evento: FormEvent) => {
    evento.preventDefault();
    const errores: ErroresFechas = esAdmin ? validarFechas(valores) : {};
    // La fecha límite la puede poner cualquiera que crea: se valida siempre, con el mensaje del contrato.
    const errorDeLimite = valores.fechaLimite ? errorFechaLimite(valores.fechaLimite) : null;
    if (errorDeLimite) errores.fechaLimite = errorDeLimite;
    setErroresCliente(errores);
    if (Object.keys(errores).length > 0) return;
    onGuardar(aEntradaApi(valores, esAdmin));
  };

  return (
    <form onSubmit={alEnviar}>
      {padre && valores.padreId === String(padre.id) && (
        <div className="formulario__madre">
          <Icono nombre="rama" />
          <span className="formulario__madre-texto" title={padre.titulo}>
            Subtarea de <strong>{formatearNumero(padre.numero)}</strong> · {padre.titulo}
          </span>
          <button
            type="button"
            className="formulario__madre-quitar"
            aria-label="Quitar la tarea madre"
            title="Quitar la tarea madre"
            onClick={() => cambiar('padreId', '')}
          >
            <Icono nombre="x" tamano={14} />
          </button>
        </div>
      )}
      <fieldset className="formulario__grupo">
        <legend>Qué es</legend>
        <div className="campo">
          <label htmlFor="tarea-titulo">Título</label>
          <input
            id="tarea-titulo"
            data-foco-inicial
            type="text"
            value={valores.titulo}
            onChange={(e) => cambiar('titulo', e.target.value)}
            maxLength={200}
            required
          />
          {onAbrirParecida && <TareasParecidas titulo={valores.titulo} onAbrir={onAbrirParecida} />}
        </div>

        <div className="formulario__rejilla">
          <div className="campo">
            <label htmlFor="tarea-estado">Estado</label>
            <select
              id="tarea-estado"
              value={valores.estado}
              onChange={(e) => cambiar('estado', e.target.value as EstadoDelFlujo)}
            >
              {ESTADOS_DEL_FLUJO.map((e) => (
                <option key={e} value={e}>
                  {ETIQUETA_ESTADO[e]}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="tarea-prioridad">Prioridad</label>
            <select
              id="tarea-prioridad"
              value={valores.prioridad}
              onChange={(e) => cambiar('prioridad', e.target.value as Prioridad)}
            >
              {PRIORIDADES.map((p) => (
                <option key={p} value={p}>
                  {etiquetaPrioridad(p)}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="tarea-tipo">Tipo</label>
            <div className="campo__con-icono">
              <IconoTipo tipo={valores.tipo} decorativo />
              <select
                id="tarea-tipo"
                value={valores.tipo}
                onChange={(e) => cambiar('tipo', e.target.value as Tipo)}
              >
                {TIPOS.map((t) => (
                  <option key={t} value={t}>
                    {ETIQUETA_TIPO[t]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="formulario__esfuerzo">
          <div className="formulario__etiqueta">
            <EtiquetaEsfuerzo />
          </div>
          <SelectorEsfuerzo
            valor={valores.esfuerzo}
            onElegir={(esfuerzo) => cambiar('esfuerzo', esfuerzo)}
          />
        </div>
      </fieldset>

      <fieldset className="formulario__grupo">
        <legend>Quién y dónde</legend>
        <div className="formulario__rejilla">
          <div className="campo">
            <label htmlFor="tarea-responsable">Responsable</label>
            <select
              id="tarea-responsable"
              value={valores.responsableId}
              onChange={(e) => cambiar('responsableId', e.target.value)}
            >
              <option value="">Sin asignar</option>
              {personas
                .filter((persona) => persona.activo || String(persona.id) === valores.responsableId)
                .map((persona) => (
                  <option key={persona.id} value={String(persona.id)}>
                    {persona.nombre}
                  </option>
                ))}
            </select>
          </div>
          <SelectorProyecto
            valor={valores.proyectoId}
            onCambio={(valor) => cambiar('proyectoId', valor)}
            puedeAgregar
          />
          <div className="campo">
            <label htmlFor="tarea-pedido">Pedido por</label>
            <input
              id="tarea-pedido"
              type="text"
              value={valores.pedidoPor}
              onChange={(e) => cambiar('pedidoPor', e.target.value)}
              maxLength={80}
            />
          </div>
          <div className="campo">
            <label htmlFor="tarea-limite">Fecha límite (opcional)</label>
            <input
              id="tarea-limite"
              type="date"
              value={valores.fechaLimite}
              onChange={(e) => {
                setErroresCliente({});
                cambiar('fechaLimite', e.target.value);
              }}
              aria-invalid={errorLimite ? true : undefined}
              aria-describedby={errorLimite ? 'tarea-limite-error' : undefined}
            />
            {/* Sin min/max: el rango lo valida `errorFechaLimite` con su mensaje (el del navegador no se controla). */}
            {errorLimite && (
              <p id="tarea-limite-error" role="alert" className="campo__error">
                {errorLimite}
              </p>
            )}
          </div>
        </div>

        {esAdmin && (
          <div className="formulario__rejilla">
            <div className="campo">
              <label htmlFor="tarea-inicio">Fecha de inicio (opcional)</label>
              <input
                id="tarea-inicio"
                type="date"
                value={valores.fechaInicio}
                max={hoy}
                onChange={(e) => cambiarFecha('fechaInicio', e.target.value)}
                aria-invalid={errorInicio ? true : undefined}
                aria-describedby={errorInicio ? 'tarea-inicio-error' : 'tarea-inicio-ayuda'}
              />
              {errorInicio ? (
                <p id="tarea-inicio-error" role="alert" className="campo__error">
                  {errorInicio}
                </p>
              ) : (
                <p id="tarea-inicio-ayuda" className="campo__ayuda">
                  Si lo dejas vacío, será hoy. Sirve para registrar tareas de días anteriores.
                </p>
              )}
            </div>
            {pideCierre && (
              <div className="campo">
                <label htmlFor="tarea-cierre">Fecha de cierre (opcional)</label>
                <input
                  id="tarea-cierre"
                  type="date"
                  value={valores.fechaCierre}
                  min={valores.fechaInicio || undefined}
                  max={hoy}
                  onChange={(e) => cambiarFecha('fechaCierre', e.target.value)}
                  aria-invalid={errorCierre ? true : undefined}
                  aria-describedby={errorCierre ? 'tarea-cierre-error' : 'tarea-cierre-ayuda'}
                />
                {errorCierre ? (
                  <p id="tarea-cierre-error" role="alert" className="campo__error">
                    {errorCierre}
                  </p>
                ) : (
                  <p id="tarea-cierre-ayuda" className="campo__ayuda">
                    Si lo dejas vacío, será hoy.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </fieldset>

      <fieldset className="formulario__grupo">
        <legend>Detalle</legend>
        <div className="campo">
          <label htmlFor="tarea-descripcion">Qué se necesita y para qué</label>
          <textarea
            id="tarea-descripcion"
            value={valores.descripcion}
            onChange={(e) => cambiar('descripcion', e.target.value)}
            maxLength={5000}
          />
        </div>
        <div className="campo">
          <label htmlFor="tarea-criterio">Cómo se comprueba que está listo</label>
          <textarea
            id="tarea-criterio"
            value={valores.criterio}
            onChange={(e) => cambiar('criterio', e.target.value)}
            maxLength={2000}
          />
        </div>
      </fieldset>

      <fieldset className="formulario__grupo">
        <legend>Producción y enlace</legend>
        <div className="formulario__marcas">
          <label>
            <input
              type="checkbox"
              checked={valores.tocaRiego}
              onChange={(e) => cambiar('tocaRiego', e.target.checked)}
            />{' '}
            Toca riego (revisión de seguridad obligatoria)
          </label>
          <label>
            <input
              type="checkbox"
              checked={valores.requiereProduccion}
              onChange={(e) => cambiar('requiereProduccion', e.target.checked)}
            />{' '}
            Pide algo a producción
          </label>
        </div>
        <div className="campo">
          <label htmlFor="tarea-produccion">Qué pide a producción</label>
          <input
            id="tarea-produccion"
            type="text"
            value={valores.detalleProduccion}
            onChange={(e) => cambiar('detalleProduccion', e.target.value)}
            maxLength={300}
          />
        </div>
        <div className="campo">
          <label htmlFor="tarea-enlace">Enlace (PR, issue o documento)</label>
          <input
            id="tarea-enlace"
            type="url"
            value={valores.enlace}
            onChange={(e) => cambiar('enlace', e.target.value)}
            placeholder="https://github.com/…"
            maxLength={500}
          />
        </div>
      </fieldset>

      <div className="formulario__acciones">
        <button
          type="submit"
          className="boton boton--primario boton--con-giro"
          disabled={guardando}
        >
          {guardando && <Giro />}
          {guardando ? etiquetaGuardando : etiquetaBoton}
        </button>
        <button type="button" className="boton" onClick={onCancelar}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
